import {alignedLines, richSegments, type RichText, type TextSegment} from '../lib/rich-text';
import {resolveVariables} from '../lib/template-variables';
import {lineCss, markCss} from './renderers/element-styles';

/**
 * Text with its marks, as the editor, the fill view and the PDF all draw it.
 *
 * Variables resolve one run at a time, after the text is cut, because a mark's
 * offsets are into the AUTHORED text: `{{job_no}}` resolving to something
 * longer first would leave every mark after it pointing at the wrong words. A
 * token split across two runs therefore stays as typed, which is also what a
 * half-bold token would mean.
 *
 * With no marks this is the plain string, so every template authored before
 * marks existed renders the node it always did. Lines become blocks only when
 * one of them carries its own alignment — see `alignedLines`.
 */
export function RichTextView({
  value,
  sampleData,
  prefix,
}: {
  value: RichText;
  /** What `{{variables}}` resolve against. */
  sampleData: Record<string, string>;
  /** Drawn before the text on its first line — a list bullet. */
  prefix?: string;
}) {
  const resolve = (t: string) => resolveVariables(t, sampleData);
  if (!value.marks?.length)
    return (
      <>
        {prefix}
        {resolve(value.text)}
      </>
    );
  const runs = (segments: TextSegment[]) =>
    segments.map((seg, i) => (
      <span key={i} style={markCss(seg.style)}>
        {resolve(seg.text)}
      </span>
    ));
  const lines = alignedLines(value);
  if (!lines)
    return (
      <>
        {prefix}
        {runs(richSegments(value))}
      </>
    );
  return (
    <>
      {lines.map((line, i) => (
        <span key={i} style={lineCss(line.align)}>
          {i === 0 && prefix}
          {runs(line.segments)}
        </span>
      ))}
    </>
  );
}
